import { getAuthToken } from '../../../../services/auth/tokenStorage';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { personalInfoService, PersonalInfo } from '../../../../services/personalInfo';
import { tasksService } from '../../../../services/tasks';
import { API_URL } from '../../../../config';
 
import { BottomNavBar } from '../../../../components/BottomNavBar';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('PersonalInfoData');

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value}</Text>
    <View style={styles.separator} />
  </View>
);

export const PersonalInfoDataScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const taskId = route.params?.taskId || 1;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  const [personalInfo, setPersonalInfo] = useState<PersonalInfo | null>(null);
  const [userProfile, setUserProfile] = useState<{ firstName: string; lastName: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadPersonalInfo();
    if (!targetUserId) {
      loadUserProfile();
    }
  }, [taskId, targetUserId]);
  
  const loadPersonalInfo = async () => {
    try {
      // Pass targetUserId to fetch another user's data (for successor view)
      const data = await personalInfoService.getPersonalInfo(taskId, targetUserId);
      if (data && data.length > 0) {
        setPersonalInfo(data[0]);
      }
    } catch (error) {
      logger.error('Error loading personal info:', error);
      Alert.alert('Error', 'Failed to load personal information.');
    } finally {
      setLoading(false);
    }
  };

  const loadUserProfile = async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        logger.debug('No token found, skipping user profile fetch');
        return;
      }

      const response = await fetch(`${API_URL}/users/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.user) {
          setUserProfile({
            firstName: result.user.firstName || '',
            lastName: result.user.lastName || '',
          });
        }
      }
    } catch (error) {
      logger.error('Error loading user profile:', error);
      // Don't show alert for profile loading errors as it's not critical
    }
  };
  
  const handleDelete = () => {
    Alert.alert(
      'Delete Personal Information',
      'Are you sure you want to delete this personal information? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: confirmDelete },
      ]
    );
  };
  
  const confirmDelete = async () => {
    if (!personalInfo) return;

    try {
      setDeleting(true);
      await personalInfoService.deletePersonalInfo(personalInfo.id);

      // CRITICAL: Check if this was the last entry and reset task if so
      const remainingData = await personalInfoService.getPersonalInfo(taskId);
      logger.debug('[PersonalInfo Debug] Remaining data after deletion:', remainingData);
      logger.debug('[PersonalInfo Debug] Remaining data length:', remainingData?.length);

      if (!remainingData || remainingData.length === 0) {
        logger.debug('[PersonalInfo Debug] No remaining data, resetting task personal_info');
        await tasksService.resetTask('personal_info');
      } else {
        logger.debug('[PersonalInfo Debug] Data still exists, not resetting task');
      }

      (navigation as any).navigate('PersonalChecklist');
    } catch (error) {
      logger.error('Error deleting personal info:', error);
      Alert.alert('Error', 'Failed to delete personal information. Please try again.');
    } finally {
      setDeleting(false);
    }
  };
  
  const formatHeight = (feet: number, inches: number) => {
    return `${feet}'${inches}"`;
  };
  
  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
          <ActivityIndicator size="large" color="#FFFFFF" />
          <Text style={[styles.title, { marginTop: 20 }]}>Loading...</Text>
        </View>
      </SafeAreaView>
    );
  }
  
  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => (navigation as any).navigate('PersonalChecklist')}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Personal Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {personalInfo ? (
            <>
              <InfoItem
                label="First Name"
                value={userProfile?.firstName || personalInfo.firstName || 'Not provided'}
              />
              <InfoItem
                label="Last Name"
                value={userProfile?.lastName || personalInfo.lastName || 'Not provided'}
              />
              <InfoItem label="Height" value={formatHeight(personalInfo.feet, personalInfo.inches)} />
              <InfoItem label="Weight" value={`${personalInfo.weight} lbs`} />
              <InfoItem label="Blood Type" value={personalInfo.bloodType} />
              <InfoItem label="Eye Color" value={personalInfo.eyeColor} />
              <InfoItem label="Hair Color" value={personalInfo.hairColor} />
              <InfoItem label="Identifying Marks" value={personalInfo.identifyingMarks || 'None'} />

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={() => (navigation as any).navigate('PersonalChecklist')}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton, deleting && { opacity: 0.7 }]}
                      onPress={handleDelete}
                      disabled={deleting}
                    >
                      {deleting ? (
                        <ActivityIndicator color="#FFFFFF" size="small" />
                      ) : (
                        <Text style={styles.buttonText}>Delete</Text>
                      )}
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={() => (navigation as any).navigate('PersonalInfoInput', { taskId })}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>
                {viewOnly ? 'This user has not added personal information yet' : 'No personal information found'}
              </Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={styles.addButton}
                  onPress={() => (navigation as any).navigate('PersonalInfoInput', { taskId })}
                >
                  <Text style={styles.addButtonText}>Add Information</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[styles.addButton, { marginTop: 10, backgroundColor: '#6B7280' }]}
                onPress={() => (navigation as any).navigate('PersonalChecklist')}
              >
                <Text style={styles.addButtonText}>Back to Checklist</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  emptyStateText: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: ms(10),
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
}); 